import {FC, useEffect, useRef, useState} from 'react'
import type React from 'react'
import Swal from 'sweetalert2'
import {api} from '../../services/api'
import {usePaginated} from '../../hooks/usePaginated'
import {Pagination} from '../../components/Pagination'

const BACKEND = process.env.REACT_APP_API_URL?.replace('/api', '') || 'https://api.qwiksurv.com'

interface ApiUser {
  id: string
  firstName: string
  lastName: string
  email: string
  phone?: string
  photo?: string
  isEmailVerified: boolean
  isActive?: boolean
  interests?: string[]
  createdAt: string
  role?: {id: number; name: string}
}

type AccountStatus = 'active' | 'banned' | 'pending'

function getInitials(u: ApiUser) {
  return ((u.firstName?.[0] ?? '') + (u.lastName?.[0] ?? '')).toUpperCase() || '?'
}

function getAccountStatus(u: ApiUser): AccountStatus {
  if (u.isActive === false) return 'banned'
  if (!u.isEmailVerified) return 'pending'
  return 'active'
}

function fmtDate(iso: string) {
  try {
    const d = new Date(iso)
    return d.toISOString().slice(0, 16).replace('T', ' ')
  } catch { return '—' }
}

const AVATAR_COLORS = ['#3b82f6', '#6366f1', '#8b5cf6', '#ec4899', '#f97316', '#22c55e']
function avatarColor(name: string) {
  let hash = 0
  for (const c of name) hash = c.charCodeAt(0) + ((hash << 5) - hash)
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

const Avatar: FC<{user: ApiUser}> = ({user}) => {
  const [failed, setFailed] = useState(false)
  const photoUrl = user.photo && !failed
    ? (user.photo.startsWith('http') || user.photo.startsWith('data:') ? user.photo : `${BACKEND}${user.photo}`)
    : null
  const bg = avatarColor(user.firstName + user.lastName)

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={user.firstName}
        onError={() => setFailed(true)}
        style={{width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0}}
      />
    )
  }
  return (
    <div style={{
      width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
      background: bg, display: 'flex', alignItems: 'center',
      justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 13,
    }}>
      {getInitials(user)}
    </div>
  )
}

const roleBadge = (role?: string) => {
  const name = role ?? 'user'
  const styles: Record<string, React.CSSProperties> = {
    user:     {background: '#f1f5f9', color: '#64748b'},
    provider: {background: '#ede9fe', color: '#7c3aed'},
    admin:    {background: '#ddd6fe', color: '#6d28d9'},
    host:     {background: '#ede9fe', color: '#7c3aed'},
  }
  const s = styles[name] ?? styles.user
  return (
    <span style={{
      ...s, padding: '3px 10px', borderRadius: 20,
      fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap',
    }}>{name}</span>
  )
}

const statusBadge = (status: AccountStatus, onDeactivate?: () => void) => {
  const styles: Record<AccountStatus, React.CSSProperties> = {
    active:  {background: '#dcfce7', color: '#16a34a'},
    banned:  {background: '#fee2e2', color: '#dc2626'},
    pending: {background: '#fef9c3', color: '#a16207'},
  }
  return (
    <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
      <span style={{...styles[status], padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500}}>{status}</span>
      {status !== 'banned' && onDeactivate && (
        <button onClick={onDeactivate} title='Deactivate' style={{
          width: 20, height: 20, borderRadius: '50%', border: '1px solid #cbd5e1',
          background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center',
          justifyContent: 'center', padding: 0, color: '#94a3b8', fontSize: 11,
        }}>✕</button>
      )}
    </div>
  )
}

const messagingBadge = (status: 'active' | 'blocked', msgCount?: number, hasAlert?: boolean) => {
  const s = status === 'active'
    ? {background: '#dcfce7', color: '#16a34a'}
    : {background: '#fee2e2', color: '#dc2626'}
  return (
    <div style={{display: 'flex', alignItems: 'center', gap: 5}}>
      <span style={{...s, padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500}}>{status}</span>
      {msgCount !== undefined && msgCount > 0 && (
        <span style={{
          background: '#3b82f6', color: '#fff', borderRadius: '50%',
          width: 20, height: 20, fontSize: 11, fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>{msgCount}</span>
      )}
      {hasAlert && (
        <span style={{
          background: '#f97316', color: '#fff', borderRadius: '50%',
          width: 20, height: 20, fontSize: 11, fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>!</span>
      )}
    </div>
  )
}

const ActionMenu: FC<{user: ApiUser; onAction: (action: string, user: ApiUser) => void}> = ({user, onAction}) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <div ref={ref} style={{position: 'relative'}}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px',
          borderRadius: 6, color: '#94a3b8', fontSize: 18, lineHeight: 1,
        }}
      >⋮</button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: '100%', background: '#fff',
          border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          zIndex: 200, minWidth: 190, overflow: 'hidden', marginTop: 4,
        }}>
          {/* Actions */}
          <div style={{padding: '6px 0'}}>
            {([
              {
                label: 'View Details', action: 'view', danger: false,
                icon: <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><path d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z'/><circle cx='12' cy='12' r='3'/></svg>,
              },
              {
                label: 'Edit Details', action: 'edit', danger: false,
                icon: <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><path d='M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7'/><path d='M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'/></svg>,
              },
              ...(!user.isEmailVerified ? [{
                label: 'Verify', action: 'verify', danger: false,
                icon: <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><polyline points='20 6 9 17 4 12'/></svg>,
              }] : []),
              user.isActive === false ? {
                label: 'Unban', action: 'unban', danger: false,
                icon: <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><path d='M9 12l2 2 4-4'/><circle cx='12' cy='12' r='10'/></svg>,
              } : {
                label: 'Ban', action: 'ban', danger: true,
                icon: <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><circle cx='12' cy='12' r='10'/><line x1='4.93' y1='4.93' x2='19.07' y2='19.07'/></svg>,
              },
              {
                label: 'Delete Account', action: 'delete', danger: true,
                icon: <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><polyline points='3 6 5 6 21 6'/><path d='M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2'/></svg>,
              },
            ] as {label: string; action: string; danger: boolean; icon: React.ReactNode}[]).map(item => (
              <button
                key={item.action}
                onClick={() => { onAction(item.action, user); setOpen(false) }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  width: '100%', textAlign: 'left', padding: '9px 14px',
                  background: 'none', border: 'none', fontSize: 13, cursor: 'pointer',
                  color: item.danger ? '#ef4444' : '#374151',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                onMouseLeave={e => (e.currentTarget.style.background = 'none')}
              >
                {item.icon}
                {item.label}
              </button>
            ))}
          </div>

        </div>
      )}
    </div>
  )
}

// ── Detail Drawer ─────────────────────────────────────────────────────────────
const UserDetailDrawer: FC<{user: ApiUser; onClose: () => void}> = ({user, onClose}) => {
  const status = getAccountStatus(user)

  const Row: FC<{label: string; value: React.ReactNode}> = ({label, value}) => (
    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f8fafc'}}>
      <span style={{fontSize: 14, color: '#94a3b8'}}>{label}</span>
      <span style={{fontSize: 14, fontWeight: 500, color: '#0f172a', textAlign: 'right'}}>{value}</span>
    </div>
  )

  const Section: FC<{title: string; children: React.ReactNode}> = ({title, children}) => (
    <div style={{padding: '20px 24px', borderBottom: '1px solid #e2e8f0'}}>
      <h4 style={{fontSize: 15, fontWeight: 700, color: '#0f172a', margin: '0 0 8px'}}>{title}</h4>
      {children}
    </div>
  )

  const activeBadge = (
    <span style={{padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: '#dcfce7', color: '#16a34a', border: '1px solid #bbf7d0'}}>Active</span>
  )

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 200}}
      />
      {/* Drawer */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 420,
        background: '#fff', zIndex: 201, display: 'flex', flexDirection: 'column',
        boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', overflowY: 'auto',
      }}>
        {/* Header */}
        <div style={{padding: '24px 24px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0}}>
          <div style={{display: 'flex', gap: 14, alignItems: 'center'}}>
            <Avatar user={user} />
            <div>
              <div style={{fontSize: 18, fontWeight: 700, color: '#0f172a'}}>{user.firstName} {user.lastName}</div>
              <div style={{fontSize: 13, color: '#94a3b8', marginTop: 2}}>{user.email}</div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 20, lineHeight: 1, padding: 4}}
          >✕</button>
        </div>

        {/* Contact Information */}
        <Section title='Contact Information'>
          <Row label='Email:' value={user.email} />
          <Row label='Phone:' value={user.phone || '—'} />
          <Row label='Interests:' value={user.interests?.length ? `${user.interests.length} selected` : '—'} />
        </Section>

        {/* Account Status */}
        <Section title='Account Status'>
          <Row label='Role:' value={<span style={{textTransform: 'capitalize'}}>{user.role?.name ?? 'User'}</span>} />
          <Row label='Account Status:' value={
            status === 'active'
              ? activeBadge
              : status === 'pending'
                ? <span style={{padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: '#fef9c3', color: '#a16207', border: '1px solid #fde68a'}}>Pending</span>
                : <span style={{padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5'}}>Banned</span>
          } />
          <Row label='Verified:' value={user.isEmailVerified ? 'Yes' : 'No'} />
          <Row label='Join Date:' value={user.createdAt ? new Date(user.createdAt).toISOString().slice(0, 10) : '—'} />
          <Row label='Last Active:' value={user.createdAt ? fmtDate(user.createdAt) : '—'} />
        </Section>

        {/* Messaging & Support */}
        <Section title='Messaging & Support'>
          <Row label='Messaging Status:' value={activeBadge} />
          <Row label='Unread Messages:' value='0' />
          <Row label='Open Support Tickets:' value='No' />
        </Section>

        {/* Activity */}
        <Section title='Activity'>
          <Row label='Total Bookings:' value='—' />
          <Row label='Total Spent:' value='—' />
        </Section>
      </div>
    </>
  )
}

// Common countries for the phone dial-code dropdown (flag + dial code).
const COUNTRIES: {code: string; name: string; dial: string; flag: string}[] = [
  {code: 'US', name: 'United States', dial: '+1', flag: '🇺🇸'},
  {code: 'CA', name: 'Canada', dial: '+1', flag: '🇨🇦'},
  {code: 'GB', name: 'United Kingdom', dial: '+44', flag: '🇬🇧'},
  {code: 'PK', name: 'Pakistan', dial: '+92', flag: '🇵🇰'},
  {code: 'IN', name: 'India', dial: '+91', flag: '🇮🇳'},
  {code: 'AE', name: 'UAE', dial: '+971', flag: '🇦🇪'},
  {code: 'SA', name: 'Saudi Arabia', dial: '+966', flag: '🇸🇦'},
  {code: 'AU', name: 'Australia', dial: '+61', flag: '🇦🇺'},
  {code: 'DE', name: 'Germany', dial: '+49', flag: '🇩🇪'},
  {code: 'FR', name: 'France', dial: '+33', flag: '🇫🇷'},
  {code: 'IT', name: 'Italy', dial: '+39', flag: '🇮🇹'},
  {code: 'ES', name: 'Spain', dial: '+34', flag: '🇪🇸'},
  {code: 'NL', name: 'Netherlands', dial: '+31', flag: '🇳🇱'},
  {code: 'SE', name: 'Sweden', dial: '+46', flag: '🇸🇪'},
  {code: 'CH', name: 'Switzerland', dial: '+41', flag: '🇨🇭'},
  {code: 'TR', name: 'Turkey', dial: '+90', flag: '🇹🇷'},
  {code: 'EG', name: 'Egypt', dial: '+20', flag: '🇪🇬'},
  {code: 'ZA', name: 'South Africa', dial: '+27', flag: '🇿🇦'},
  {code: 'NG', name: 'Nigeria', dial: '+234', flag: '🇳🇬'},
  {code: 'BD', name: 'Bangladesh', dial: '+880', flag: '🇧🇩'},
  {code: 'ID', name: 'Indonesia', dial: '+62', flag: '🇮🇩'},
  {code: 'MY', name: 'Malaysia', dial: '+60', flag: '🇲🇾'},
  {code: 'SG', name: 'Singapore', dial: '+65', flag: '🇸🇬'},
  {code: 'PH', name: 'Philippines', dial: '+63', flag: '🇵🇭'},
  {code: 'CN', name: 'China', dial: '+86', flag: '🇨🇳'},
  {code: 'JP', name: 'Japan', dial: '+81', flag: '🇯🇵'},
  {code: 'BR', name: 'Brazil', dial: '+55', flag: '🇧🇷'},
  {code: 'MX', name: 'Mexico', dial: '+52', flag: '🇲🇽'},
  {code: 'QA', name: 'Qatar', dial: '+974', flag: '🇶🇦'},
  {code: 'KW', name: 'Kuwait', dial: '+965', flag: '🇰🇼'},
]

// Split a stored phone (e.g. "+923001234567") into its dial code + local number.
function parsePhone(raw?: string): {dial: string; local: string} {
  const s = (raw || '').replace(/[^\d+]/g, '')
  if (s.startsWith('+')) {
    const match = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length).find(c => s.startsWith(c.dial))
    if (match) return {dial: match.dial, local: s.slice(match.dial.length)}
    return {dial: '+1', local: s.replace('+', '')}
  }
  return {dial: '+1', local: s}
}

// Defined at module scope (NOT inside the modal) so it isn't recreated on every
// keystroke — otherwise React remounts the inputs and they lose focus.
const EditField: FC<{label: string; children: React.ReactNode}> = ({label, children}) => (
  <div style={{display: 'flex', flexDirection: 'column', gap: 6}}>
    <label style={{fontSize: 13, fontWeight: 600, color: '#475569'}}>{label}</label>
    {children}
  </div>
)

// ── Edit User Modal ───────────────────────────────────────────────────────────
const UserEditModal: FC<{user: ApiUser; onClose: () => void; onSaved: () => void}> = ({user, onClose, onSaved}) => {
  const init = parsePhone(user.phone)
  const [firstName, setFirstName] = useState(user.firstName || '')
  const [lastName, setLastName]   = useState(user.lastName || '')
  const [email, setEmail]         = useState(user.email || '')
  const [dial, setDial]           = useState(init.dial)
  const [local, setLocal]         = useState(init.local)
  const [saving, setSaving]       = useState(false)
  const [err, setErr]             = useState('')

  const inputStyle: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', padding: '12px 14px',
    border: '1px solid #d1d5db', borderRadius: 9, fontSize: 15, outline: 'none',
  }

  const save = async () => {
    if (!firstName.trim()) { setErr('First name is required'); return }
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setErr('Enter a valid email'); return }
    setErr(''); setSaving(true)
    try {
      const digits = local.replace(/[^\d]/g, '')
      const phone = digits ? `${dial}${digits}` : ''
      await api.updateUser(user.id, {firstName: firstName.trim(), lastName: lastName.trim(), email: email.trim(), phone})
      onSaved()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to save changes')
      setSaving(false)
    }
  }

  return (
    <>
      <div onClick={onClose} style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 300}} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: 460, maxWidth: 'calc(100vw - 32px)', background: '#fff', borderRadius: 16,
        boxShadow: '0 24px 64px rgba(0,0,0,0.25)', zIndex: 301, display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{padding: '20px 24px', borderBottom: '1px solid #eef2f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <h3 style={{margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a'}}>Edit User</h3>
          <button onClick={onClose} style={{background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 20, lineHeight: 1}}>✕</button>
        </div>

        {/* Body */}
        <div style={{padding: 24, display: 'flex', flexDirection: 'column', gap: 16}}>
          <EditField label='First name'>
            <input style={inputStyle} value={firstName} onChange={e => setFirstName(e.target.value)} placeholder='First name' />
          </EditField>
          <EditField label='Last name'>
            <input style={inputStyle} value={lastName} onChange={e => setLastName(e.target.value)} placeholder='Last name' />
          </EditField>
          <EditField label='Email'>
            <input style={inputStyle} type='email' value={email} onChange={e => setEmail(e.target.value)} placeholder='Email' />
          </EditField>
          <EditField label='Phone number'>
            <div style={{display: 'flex', gap: 8}}>
              <select
                value={dial}
                onChange={e => setDial(e.target.value)}
                style={{...inputStyle, width: 'auto', flexShrink: 0, background: '#fff', cursor: 'pointer'}}
              >
                {COUNTRIES.map(c => (
                  <option key={c.code} value={c.dial}>{c.flag} {c.dial}</option>
                ))}
              </select>
              <input
                style={{...inputStyle, flex: 1}}
                type='tel'
                value={local}
                onChange={e => setLocal(e.target.value)}
                placeholder='Phone number'
              />
            </div>
          </EditField>

          {err ? <div style={{fontSize: 13, color: '#dc2626'}}>{err}</div> : null}
        </div>

        {/* Footer */}
        <div style={{padding: '16px 24px', borderTop: '1px solid #eef2f6', display: 'flex', justifyContent: 'flex-end', gap: 10}}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{padding: '10px 18px', borderRadius: 9, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 14, fontWeight: 600, cursor: 'pointer'}}
          >Cancel</button>
          <button
            onClick={save}
            disabled={saving}
            style={{padding: '10px 20px', borderRadius: 9, border: 'none', background: '#6d28d9', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.7 : 1}}
          >{saving ? 'Saving…' : 'Save changes'}</button>
        </div>
      </div>
    </>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
// Reused by the Users, Drivers and Business tabs. `role` scopes the list to one
// account type server-side; when absent it shows all three.
interface UsersPageProps {
  role?: 'user' | 'driver' | 'business'
  pageTitle?: string
  pageSubtitle?: string
}

const UsersManagementPage: FC<UsersPageProps> = ({
  role,
  pageTitle = 'Users',
  pageSubtitle = 'Manage user accounts and permissions',
}) => {
  const [statusFilter, setStatusFilter] = useState('all')
  const [detailUser, setDetailUser] = useState<ApiUser | null>(null)
  const [editUser, setEditUser] = useState<ApiUser | null>(null)

  const {items: users, total, page, setPage, search, setSearch, limit, loading, error, reload} =
    usePaginated<ApiUser>(
      (p, l, s) => api.getUsersPaged(p, l, s, {
        status: statusFilter !== 'all' ? statusFilter : undefined,
        role,
      }) as Promise<{items: ApiUser[]; total: number; page: number; limit: number}>,
      {limit: 20, deps: [statusFilter, role]},
    )

  const toast = (title: string, icon: 'success' | 'error' = 'success') =>
    Swal.fire({
      toast: true, position: 'top-end', icon, title,
      showConfirmButton: false, timer: 2600, timerProgressBar: true,
    })

  const handleAction = async (action: string, user: ApiUser) => {
    const fullName = `${user.firstName} ${user.lastName}`.trim() || user.email

    if (action === 'view') { setDetailUser(user); return }
    if (action === 'edit') { setEditUser(user); return }

    try {
      if (action === 'delete') {
        const res = await Swal.fire({
          title: 'Delete account?',
          html: `<b>${fullName}</b>'s account will be permanently removed. This cannot be undone.`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, delete',
          confirmButtonColor: '#ef4444',
          cancelButtonText: 'Cancel',
        })
        if (!res.isConfirmed) return
        await api.deleteUser(user.id)
        await reload()
        toast(`${fullName} deleted`)
        return
      }

      if (action === 'unban') {
        await api.unbanUser(user.id)
        await reload()
        toast(`${fullName} unbanned`)
        return
      }

      if (action === 'ban') {
        const res = await Swal.fire({
          title: 'Ban user?',
          html: `<b>${fullName}</b> will lose access to their account.`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, ban',
          confirmButtonColor: '#ef4444',
          cancelButtonText: 'Cancel',
        })
        if (!res.isConfirmed) return
        await api.banUser(user.id)
        await reload()
        toast(`${fullName} banned`)
        return
      }

    } catch (err) {
      Swal.fire({
        title: 'Action failed',
        text: err instanceof Error ? err.message : 'Something went wrong. Please try again.',
        icon: 'error',
        confirmButtonColor: '#3b82f6',
      })
    }
  }

  return (
    <div>
      {/* Detail Drawer */}
      {detailUser && <UserDetailDrawer user={detailUser} onClose={() => setDetailUser(null)} />}

      {/* Edit Modal */}
      {editUser && (
        <UserEditModal
          user={editUser}
          onClose={() => setEditUser(null)}
          onSaved={() => { setEditUser(null); reload(); toast('User updated') }}
        />
      )}

      {/* Header */}
      <div style={{marginBottom: 24}}>
        <h1 style={{fontSize: 24, fontWeight: 700, color: '#0f172a', margin: '0 0 4px'}}>{pageTitle}</h1>
        <p style={{fontSize: 14, color: '#64748b', margin: 0}}>{pageSubtitle}</p>
      </div>

      {/* Filters */}
      <div style={{
        background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
        padding: '16px 20px', marginBottom: 16,
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center',
      }}>
        {/* Search */}
        <div style={{position: 'relative', flex: '1 1 260px'}}>
          <svg style={{position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8'}} width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
            <circle cx='11' cy='11' r='8'/><line x1='21' y1='21' x2='16.65' y2='16.65'/>
          </svg>
          <input
            type='text'
            placeholder='Search by name or email...'
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '10px 12px 10px 38px', border: '1px solid #e2e8f0',
              borderRadius: 8, fontSize: 14, color: '#0f172a', background: '#f8fafc',
              outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>


        {/* Status filter */}
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          style={{flex: '0 0 180px', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, color: '#374151', background: '#f8fafc', cursor: 'pointer', outline: 'none'}}
        >
          <option value='all'>All Status</option>
          <option value='active'>Active</option>
          <option value='banned'>Banned</option>
        </select>

        {/* Refresh */}
        <button
          onClick={reload}
          disabled={loading}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px',
            border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14,
            fontWeight: 500, color: '#374151', background: '#f8fafc', cursor: 'pointer',
          }}
        >
          <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' style={{animation: loading ? 'spin 1s linear infinite' : 'none'}}>
            <polyline points='23 4 23 10 17 10'/><polyline points='1 20 1 14 7 14'/>
            <path d='M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15'/>
          </svg>
          Refresh
        </button>
      </div>

      {/* Error */}
      {error && (
        <div style={{padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 14, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <span>{error}</span>
          <button onClick={reload} style={{color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, fontSize: 13}}>Retry</button>
        </div>
      )}

      {/* Table */}
      <div style={{background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden'}}>
        {loading ? (
          <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px 0'}}>
            <div style={{width: 32, height: 32, border: '3px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite'}} />
          </div>
        ) : (
          <div style={{overflowX: 'auto'}}>
            <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 14}}>
              <thead>
                <tr style={{background: '#f8fafc', borderBottom: '1px solid #e2e8f0'}}>
                  {['USER', 'ROLE', 'ACCOUNT STATUS', 'MESSAGING', 'PHONE', 'JOINED', 'ACTIONS'].map(h => (
                    <th key={h} style={{
                      padding: '11px 20px', textAlign: 'left', fontSize: 11,
                      fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase',
                      letterSpacing: '0.06em', whiteSpace: 'nowrap',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u, i) => {
                  const status = getAccountStatus(u)
                  return (
                    <tr
                      key={u.id}
                      style={{borderBottom: i < users.length - 1 ? '1px solid #f1f5f9' : 'none'}}
                      onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      {/* User */}
                      <td style={{padding: '14px 20px', minWidth: 220}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                          <Avatar user={u} />
                          <div>
                            <div style={{fontWeight: 600, color: '#0f172a'}}>{u.firstName} {u.lastName}</div>
                            <div style={{fontSize: 12, color: '#94a3b8', marginTop: 1}}>{u.email}</div>
                          </div>
                        </div>
                      </td>

                      {/* Role */}
                      <td style={{padding: '14px 20px'}}>
                        {roleBadge(u.role?.name)}
                      </td>

                      {/* Account Status */}
                      <td style={{padding: '14px 20px'}}>
                        {statusBadge(status, status !== 'banned' ? () => handleAction('ban', u) : undefined)}
                      </td>

                      {/* Messaging */}
                      <td style={{padding: '14px 20px'}}>
                        {messagingBadge('active')}
                      </td>

                      {/* Phone */}
                      <td style={{padding: '14px 20px', color: '#64748b', whiteSpace: 'nowrap'}}>
                        {u.phone || '—'}
                      </td>

                      {/* Joined */}
                      <td style={{padding: '14px 20px', color: '#64748b', whiteSpace: 'nowrap'}}>
                        {fmtDate(u.createdAt)}
                      </td>

                      {/* Actions */}
                      <td style={{padding: '14px 20px', textAlign: 'right'}}>
                        <ActionMenu user={u} onAction={handleAction} />
                      </td>
                    </tr>
                  )
                })}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{padding: '48px 20px', textAlign: 'center', color: '#94a3b8'}}>
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer */}
        {!loading && (
          <div style={{padding: '12px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <span style={{fontSize: 13, color: '#94a3b8'}}>Showing {users.length} of {total} users</span>
            <div style={{display: 'flex', gap: 8}}>
              {[
                {label: 'Active',  count: users.filter(u => getAccountStatus(u) === 'active').length,  bg: '#dcfce7', color: '#16a34a'},
                {label: 'Pending', count: users.filter(u => getAccountStatus(u) === 'pending').length, bg: '#fef9c3', color: '#a16207'},
              ].map(s => (
                <span key={s.label} style={{...s, padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500}}>{s.label}: {s.count}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      <Pagination page={page} limit={limit} total={total} onPageChange={setPage} />

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

const UsersPageWrapper: FC = () => <UsersManagementPage />

export {UsersPageWrapper, UsersManagementPage}
